import { Icon, Spinner } from "@sarva/ui";
import { lazy, Suspense, useEffect, useState, type ComponentType } from "react";
import { createBrowserRouter, Link, RouterProvider } from "react-router-dom";
import {
  FORGOT_PASSWORD_ROUTE,
  ForgotPasswordPage,
  LAUNCH_ROUTE,
  RESET_PASSWORD_ROUTE,
  ResetPasswordPage,
  LaunchPage,
  RequireAuth,
  SIGN_IN_ROUTE,
  CORE_SIGN_IN_ROUTE,
  CoreSignInPage,
  SignInPage,
} from "@/features/auth";
import { AppLayout } from "./layout";

// doc 27 §8: route-level code splitting; features are lazy-loaded, so the
// initial route ships the shell and one screen rather than all of them.
//
// The sign-in screen is NOT lazy: it is the first thing an unauthenticated
// visitor sees, and a spinner in front of a spinner is not a loading state.
//
// (H) Every screen is **prefetched** once the app is idle (`prefetchScreens`), and the
// fallback waits 400 ms before it shows. Without both, every first visit to a screen flashed
// "Loading…" while its chunk downloaded — between every step of a demo. The chunks are
// static code, no data and no PHI, so fetching them early costs bandwidth and nothing else.
const screenLoaders: (() => Promise<unknown>)[] = [];

function screen<P extends object>(load: () => Promise<{ default: ComponentType<P> }>) {
  screenLoaders.push(load);
  return lazy(load);
}

/** Download every screen's code in the background, a few at a time, once the app is idle. */
function prefetchScreens(): void {
  const queue = [...screenLoaders];
  const next = (): void => {
    const batch = queue.splice(0, 4);
    if (batch.length === 0) return;
    // A failed prefetch is not an error: the screen loads on demand as it always did.
    void Promise.allSettled(batch.map(async (load) => load())).then(next);
  };
  next();
}

const PracticesPage = screen(async () => ({
  default: (await import("@/features/practices")).PracticesPage,
}));
const ImportsPage = screen(async () => ({
  default: (await import("@/features/imports")).ImportsPage,
}));
const ImportBatchPage = screen(async () => ({
  default: (await import("@/features/imports")).ImportBatchPage,
}));
const PayersPage = screen(async () => ({
  default: (await import("@/features/payers")).PayersPage,
}));
const NoteEditorPage = screen(async () => ({
  default: (await import("@/features/clinical")).NoteEditorPage,
}));
const ClinicalSetupPage = screen(async () => ({
  default: (await import("@/features/clinical")).ClinicalSetupPage,
}));
const TemplateEditorPage = screen(async () => ({
  default: (await import("@/features/clinical")).TemplateEditorPage,
}));
const CertificationChasePage = screen(async () => ({
  default: (await import("@/features/clinical")).CertificationChasePage,
}));
const CosignQueuePage = screen(async () => ({
  default: (await import("@/features/clinical")).CosignQueuePage,
}));
const PlanSignoffPage = screen(async () => ({
  default: (await import("@/features/clinical")).PlanSignoffPage,
}));
const IntakePage = screen(async () => ({
  default: (await import("@/features/clinical")).IntakePage,
}));
const ReminderPage = screen(async () => ({
  default: (await import("@/features/reminders")).ReminderPage,
}));
const BookingPage = screen(async () => ({
  default: (await import("@/features/online-booking")).BookingPage,
}));
const PatientChartPage = screen(async () => ({
  default: (await import("@/features/patients")).PatientChartPage,
}));
const PatientBySourcePage = screen(async () => ({
  default: (await import("@/features/patients")).PatientBySourcePage,
}));
const PatientEligibilityPage = screen(async () => ({
  default: (await import("@/features/eligibility")).PatientEligibilityPage,
}));
const DashboardPage = screen(async () => ({
  default: (await import("@/features/dashboard")).DashboardPage,
}));
const WorkQueuePage = screen(async () => ({
  default: (await import("@/features/work-queue")).WorkQueuePage,
}));
const ClaimsPage = screen(async () => ({
  default: (await import("@/features/claims")).ClaimsPage,
}));
const ClaimDetailPage = screen(async () => ({
  default: (await import("@/features/claims")).ClaimDetailPage,
}));
const DenialsPage = screen(async () => ({
  default: (await import("@/features/denials")).DenialsPage,
}));
const DenialReviewPage = screen(async () => ({
  default: (await import("@/features/denials")).DenialReviewPage,
}));
const AppealsPage = screen(async () => ({
  default: (await import("@/features/appeals")).AppealsPage,
}));
const RemittancePage = screen(async () => ({
  default: (await import("@/features/remittance")).RemittancePage,
}));
const PostingPage = screen(async () => ({
  default: (await import("@/features/posting")).PostingPage,
}));
const PatientsPage = screen(async () => ({
  default: (await import("@/features/patients")).PatientsPage,
}));
const ReportsIndexPage = screen(async () => ({
  default: (await import("@/features/reports")).ReportsIndexPage,
}));
const PracticeActivityPage = screen(async () => ({
  default: (await import("@/features/reports")).PracticeActivityPage,
}));
const ArAgingPage = screen(async () => ({
  default: (await import("@/features/reports")).ArAgingPage,
}));
const BalanceDriftPage = screen(async () => ({
  default: (await import("@/features/reports")).BalanceDriftPage,
}));
const ReportExportsPage = screen(async () => ({
  default: (await import("@/features/reports")).ReportExportsPage,
}));
const ScoreboardPage = screen(async () => ({
  default: (await import("@/features/scoreboard")).ScoreboardPage,
}));
const PayerRulesPage = screen(async () => ({
  default: (await import("@/features/payer-rules")).PayerRulesPage,
}));
const ScrubRulesPage = screen(async () => ({
  default: (await import("@/features/scrub-rules")).ScrubRulesPage,
}));
const BankReconciliationPage = screen(async () => ({
  default: (await import("@/features/bank-reconciliation"))
    .BankReconciliationPage,
}));
const MonthEndPage = screen(async () => ({
  default: (await import("@/features/month-end")).MonthEndPage,
}));
const RecoveryPage = screen(async () => ({
  default: (await import("@/features/recovery")).RecoveryPage,
}));
const FeeSchedulesPage = screen(async () => ({
  default: (await import("@/features/fee-schedules")).FeeSchedulesPage,
}));
const ProvidersPage = screen(async () => ({
  default: (await import("@/features/providers")).ProvidersPage,
}));
const PatientBalancePage = screen(async () => ({
  default: (await import("@/features/patient-balance")).PatientBalancePage,
}));
const RefundsPage = screen(async () => ({
  default: (await import("@/features/refunds")).RefundsPage,
}));
const AdjustmentsPage = screen(async () => ({
  default: (await import("@/features/adjustments")).AdjustmentsPage,
}));
const RotaSetupPage = screen(async () => ({
  default: (await import("@/features/scheduling-templates")).RotaSetupPage,
}));
const CodingPage = screen(async () => ({
  default: (await import("@/features/coding")).CodingPage,
}));
const CodingReviewPage = screen(async () => ({
  default: (await import("@/features/coding")).CodingReviewPage,
}));
const ObligationsPage = screen(async () => ({
  default: (await import("@/features/obligations")).ObligationsPage,
}));
const DueFollowUpPage = screen(async () => ({
  default: (await import("@/features/follow-up-notes")).DueFollowUpPage,
}));
const WorkItemPage = screen(async () => ({
  default: (await import("@/features/work-queue")).WorkItemPage,
}));
const ObligationDetailPage = screen(async () => ({
  default: (await import("@/features/obligations")).ObligationDetailPage,
}));
const ClaimSubmissionsPage = screen(async () => ({
  default: (await import("@/features/claim-submissions")).ClaimSubmissionsPage,
}));
const AgentsPage = screen(async () => ({
  default: (await import("@/features/agents")).AgentsPage,
}));
const AppealDetailPage = screen(async () => ({
  default: (await import("@/features/appeals")).AppealDetailPage,
}));
const PolicyBandsPage = screen(async () => ({
  default: (await import("@/features/policy-bands")).PolicyBandsPage,
}));
const LocationsPage = screen(async () => ({
  default: (await import("@/features/locations")).LocationsPage,
}));
const EmailLogPage = screen(async () => ({
  default: (await import("@/features/email-log")).EmailLogPage,
}));
const AuditPage = screen(async () => ({
  default: (await import("@/features/audit")).AuditPage,
}));
const SettingsPage = screen(async () => ({
  default: (await import("@/features/settings")).SettingsPage,
}));
const AdministrationPage = screen(async () => ({
  default: (await import("@/features/administration")).AdministrationPage,
}));
const SecurityPage = screen(async () => ({
  default: (await import("@/features/security")).SecurityPage,
}));
const ServiceMapPage = screen(async () => ({
  default: (await import("@/features/service-map")).ServiceMapPage,
}));
const CodeSetsPage = screen(async () => ({
  default: (await import("@/features/charge-capture")).CodeSetsPage,
}));
const ChargeCapturePage = screen(async () => ({
  default: (await import("@/features/charge-capture")).ChargeCapturePage,
}));
const SchedulePage = screen(async () => ({
  default: (await import("@/features/schedule")).SchedulePage,
}));
const ReferralsPage = screen(async () => ({
  default: (await import("@/features/referrals")).ReferralsPage,
}));
const PriorAuthPage = screen(async () => ({
  default: (await import("@/features/prior-auth")).PriorAuthPage,
}));
const DocumentsPage = screen(async () => ({
  default: (await import("@/features/documents")).DocumentsPage,
}));
const WritebackQueuePage = screen(async () => ({
  default: (await import("@/features/writeback")).WritebackQueuePage,
}));
const AcknowledgementsPage = screen(async () => ({
  default: (await import("@/features/acknowledgements")).AcknowledgementsPage,
}));

function RouteFallback() {
  // Nothing for the first 400 ms: a prefetched screen resolves well inside that, and a
  // spinner that appears for one frame reads as flicker, not as a loading state.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 400);
    return () => window.clearTimeout(timer);
  }, []);
  if (!visible) return null;
  return (
    <div
      className="flex items-center gap-2 py-12 text-sm text-secondary"
      role="status"
    >
      <Spinner label="Loading this screen" size={5} />
      Loading…
    </div>
  );
}

function NotFound() {
  // doc 27 §6, the 404 row: a not-found state within the current layout, with
  // a way back — never a blank screen. `display-type` is permitted here
  // because (H) doc 41 §1 allows Domine on an empty state, and this page shows
  // no money, no PHI and no data grid.
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <Icon name="search" size={7} className="text-tertiary" />
      <h1 className="display-type text-2xl text-default">
        That page does not exist
      </h1>
      <p className="max-w-prose text-sm text-secondary">
        The link may be out of date. Practices is the live configuration screen,
        and the work queue is where the day's items are.
      </p>
      <div className="mt-2 flex gap-2">
        <Link
          to="/"
          className="rounded-sm border border-control px-3 py-1.5 text-sm font-medium text-default no-underline hover:border-strong"
        >
          Go to the dashboard
        </Link>
        <Link
          to="/practices"
          className="rounded-sm bg-primary px-3 py-1.5 text-sm font-medium text-on-primary no-underline hover:bg-primary-hover"
        >
          Open practices
        </Link>
      </div>
    </div>
  );
}

const lazyRoute = (element: React.ReactNode) => (
  <Suspense fallback={<RouteFallback />}>{element}</Suspense>
);

export const routes = [
  // Unauthenticated. Outside `<AppLayout>` on purpose: a sign-in screen inside
  // a navigation shell offers links the visitor cannot follow.
  { path: SIGN_IN_ROUTE, element: <SignInPage /> },
  { path: CORE_SIGN_IN_ROUTE, element: <CoreSignInPage /> },
  // Not lazy, for the sign-in screen's reason: it is the first thing a visitor arriving from a
  // partner product sees, and the assertion in its fragment is good for seconds.
  { path: LAUNCH_ROUTE, element: <LaunchPage /> },
  // Forgot / reset password (backend D173). Signed out by definition, so outside the shell.
  { path: FORGOT_PASSWORD_ROUTE, element: <ForgotPasswordPage /> },
  { path: RESET_PASSWORD_ROUTE, element: <ResetPasswordPage /> },
  // A patient's intake form (D169). No session: the single-use link is the authority, so it
  // sits outside the authenticated shell like sign-in — a navigation rail would offer a
  // patient links into the practice's system.
  { path: "intake/:token", element: lazyRoute(<IntakePage />) },
  // A referring physician certifying a plan of care (D179): the link is the authority, as above.
  { path: "plan-signoff/:token", element: lazyRoute(<PlanSignoffPage />) },
  // A practice's public booking page (D200): the published link is the authority, as above.
  { path: "book/:token", element: lazyRoute(<BookingPage />) },
  // A patient answering their appointment reminder (D292): the emailed link is the authority.
  { path: "reminder/:token", element: lazyRoute(<ReminderPage />) },
  {
    path: "/",
    // (H) doc 27 §6, the `401` row: everything below this guard requires a
    // session, and losing one returns here with the attempted location intact.
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: lazyRoute(<DashboardPage />) },
      { path: "practices", element: lazyRoute(<PracticesPage />) },
      { path: "imports", element: lazyRoute(<ImportsPage />) },
      { path: "payers", element: lazyRoute(<PayersPage />) },
      // A detail route under a built list, reached from a row, so it needs no nav entry —
      // `nav.built.test.ts` ties *leaves* to routes.
      { path: "imports/:importId", element: lazyRoute(<ImportBatchPage />) },
      // Eligibility belongs to a patient, so it is reached from a patient row rather than
      // from the rail — the same shape as the denial review screen.
      {
        path: "patients/:patientId/eligibility",
        element: lazyRoute(<PatientEligibilityPage />),
      },
      // The patient hub. Every per-patient endpoint was keyed by an id nothing
      // produced, so this route did not exist and `/patients/{id}` answered that
      // the page did not exist — which an SME review recorded as the product's
      // biggest usability gap. Reached from a roster row, never from the rail.
      { path: "patients/:patientId", element: lazyRoute(<PatientChartPage />) },
      // A partner's link to one of its own customers (a point of sale's guest card): looked up
      // by `external_patient_id`, then the chart's Claims & payments. Reached from outside.
      {
        path: "patients/by-source/:system/:externalId",
        element: lazyRoute(<PatientBySourcePage />),
      },
      // A clinical note (ADR 0017), reached from the patient's chart — never from the rail,
      // because it needs a row id.
      { path: "clinical-notes/:noteId", element: lazyRoute(<NoteEditorPage />) },
      { path: "clinical-setup", element: lazyRoute(<ClinicalSetupPage />) },
      { path: "cosign-queue", element: lazyRoute(<CosignQueuePage />) },
      { path: "plan-certifications", element: lazyRoute(<CertificationChasePage />) },
      { path: "clinical-setup/templates/:templateId", element: lazyRoute(<TemplateEditorPage />) },
      { path: "queue", element: lazyRoute(<WorkQueuePage />) },
      // (H) The item itself, which always exists. `Enter` on the queue used to
      // open the item's *subject*, so an item whose subject has no screen went
      // nowhere — `useWorkQueue` flagged that as "the same defect as a row
      // nothing can act on", and it was the whole screen's: `:resolve` had no
      // caller at all. The subject is one link from here.
      { path: "queue/:workItemId", element: lazyRoute(<WorkItemPage />) },
      // The AR worklist. Registered *before* the detail route for readability
      // only — react-router ranks a static segment above a dynamic one, so
      // `/claims` could not be swallowed by `claims/:claimNumber` either way.
      //
      // This is what the sidebar's "Claims" entry points at. It used to point at
      // `/claims/CLM-2026-004417`, a claim number hardcoded from one tenant,
      // which rendered the not-found page everywhere else — and `nav.built.test`
      // passed the whole time, because the *route* existed. See `ClaimsPage`.
      { path: "claims", element: lazyRoute(<ClaimsPage />) },
      { path: "claims/:claimNumber", element: lazyRoute(<ClaimDetailPage />) },
      { path: "denials", element: lazyRoute(<DenialsPage />) },
      // doc 27 §10 screen 2. A detail route under a built list, so it needs no
      // nav entry of its own — `nav.built.test.ts` ties *leaves* to routes, and
      // this is reached from a row.
      { path: "denials/:denialId", element: lazyRoute(<DenialReviewPage />) },
      { path: "appeals", element: lazyRoute(<AppealsPage />) },
      // The three steps an appeal goes through. Reached from a row, never from
      // the rail: the nav test refuses a parameterised nav target, because a
      // claim number hardcoded into the sidebar was a real defect once.
      { path: "appeals/:appealId", element: lazyRoute(<AppealDetailPage />) },
      // The nav writes this one singular ("Remittance") because a remittance
      // advice is the thing, and the API's collection is `/remittances`. The
      // route follows the nav, and `nav.built.test.ts` is what keeps the two
      // from drifting apart.
      { path: "remittance", element: lazyRoute(<RemittancePage />) },
      { path: "posting", element: lazyRoute(<PostingPage />) },
      { path: "patients", element: lazyRoute(<PatientsPage />) },
      // ── Reports: four routes where there was one ─────────────────────────
      //
      // `/reports` carried eight jobs — AR aging, the ledger tie-out sweep, the
      // charge master, exports, and four ranked worklists — at 1,591 DOM
      // elements and ten viewports (doc 43, D35). A route is the unit of
      // linking, bookmarking and permission, so eight jobs on one route could
      // not be sent to a colleague and could not be granted apart: exports
      // declare `report:export` while the two reports declare `report:read`.
      //
      // The index stays at `/reports` and the three reports are its children.
      // **None of them is a nav leaf**, deliberately: `nav.built.test.ts`
      // refuses a multi-segment nav target, because the sidebar is one string
      // for every tenant and a deep link is how a hardcoded row id got into the
      // rail once. They are reached from the index's cards, which is the same
      // ruling — a destination below a screen is reached from that screen.
      { path: "reports", element: lazyRoute(<ReportsIndexPage />) },
      { path: "activity", element: lazyRoute(<PracticeActivityPage />) },
      { path: "reports/ar-aging", element: lazyRoute(<ArAgingPage />) },
      {
        path: "reports/balance-drift",
        element: lazyRoute(<BalanceDriftPage />),
      },
      { path: "reports/exports", element: lazyRoute(<ReportExportsPage />) },
      // M10's one real aggregate. Separate from Reports on purpose: Reports is
      // four ranked samples over live lists, the scoreboard is a read model a
      // projection computed, and presenting them as one surface would invite
      // reading a sample as a total.
      { path: "scoreboard", element: lazyRoute(<ScoreboardPage />) },
      // The human-in-the-loop surface for `payer_policy_rule_extraction`
      // (D59). ADR 0002's "the system commits" is a button on this screen.
      { path: "payer-rules", element: lazyRoute(<PayerRulesPage />) },
      { path: "scrub-rules", element: lazyRoute(<ScrubRulesPage />) },
      // GAP-062. The screen that closes a month — `accounting_periods` and the
      // trigger that enforces it have existed since migration 0012 and nothing
      // could close one, so the guard had guarded nothing.
      { path: "month-end", element: lazyRoute(<MonthEndPage />) },
      // GAP-041. The other side of the month-end close: what the bank says
      // arrived, matched against the remittances that explain it.
      {
        path: "bank-reconciliation",
        element: lazyRoute(<BankReconciliationPage />),
      },
      // The Phase 2 money surfaces. Each is the UI over a module that was
      // reachable only through `/api/v1` — the rules and the money paths had to
      // be right first, but a scoreboard a practice cannot open is not a
      // scoreboard delivered.
      { path: "recovery", element: lazyRoute(<RecoveryPage />) },
      { path: "fee-schedules", element: lazyRoute(<FeeSchedulesPage />) },
      { path: "providers", element: lazyRoute(<ProvidersPage />) },
      { path: "patient-balance", element: lazyRoute(<PatientBalancePage />) },
      // GAP-059. Money going back out, beside the screen that shows what is
      // owed in — the two halves of a patient's account.
      { path: "refunds", element: lazyRoute(<RefundsPage />) },
      // M17. The path is `write-offs` rather than `adjustments` because that is
      // what the practice calls it; the resource keeps the API's name.
      { path: "write-offs", element: lazyRoute(<AdjustmentsPage />) },
      // M12. The worklist, and one review. There is no `GET /coding-reviews`,
      // so a review is reached from the encounter it was opened for or by its
      // own URL — a browser-side list of ids would be a worklist that is wrong
      // on another machine.
      // M01's configuration. Nine uncalled operations until this screen.
      { path: "scheduling-setup", element: lazyRoute(<RotaSetupPage />) },
      { path: "coding", element: lazyRoute(<CodingPage />) },
      { path: "coding/:reviewId", element: lazyRoute(<CodingReviewPage />) },
      // The deadline engine's own screen. It is last to arrive and it is the
      // product's central claim: until it existed, obligations were visible six
      // at a time on the dashboard and one claim at a time on the claim page,
      // and "what breaks first across everything" had nowhere to be asked.
      { path: "obligations", element: lazyRoute(<ObligationsPage />) },
      // (H) Its own route, not only a dashboard panel. The SME's Build 3 review logged
      // "No due-follow-up worklist found" while the panel was on the dashboard the
      // whole time — somebody looking for a worklist looks for a worklist, and in a
      // gap review an undiscoverable feature scores the same as a missing one.
      { path: "follow-ups", element: lazyRoute(<DueFollowUpPage />) },
      // The only place a deadline can be closed. Not a row action, deliberately:
      // a coded reason from a nineteen-member vocabulary, each member with a
      // sentence explaining which number it moves, does not fit a table cell —
      // and `useObligations.ts` has said since M02 that a closure one click
      // from a row would let a deadline be closed without the fact that closes
      // it.
      {
        path: "obligations/:obligationId",
        element: lazyRoute(<ObligationDetailPage />),
      },
      // Filing attempts nobody has resolved, across claims. It was a list
      // screen without a list endpoint — fanning out over one page of claims
      // and disclaiming the gap in a banner — until `GET /claim-submissions`
      // landed with the five filters the workaround's own docstring had asked
      // for. See `features/claim-submissions/api/claimSubmissions.ts`.
      {
        path: "claim-submissions",
        element: lazyRoute(<ClaimSubmissionsPage />),
      },
      // Agent activity, gated on `work_item:read` rather than on
      // `automation:read` — no endpoint declares the latter, and the only thing
      // the contract can answer about automated work is R-WM-07's queue health.
      { path: "agents", element: lazyRoute(<AgentsPage />) },
      // The one screen in the product that makes no request. Nothing reads a
      // policy band, so it reports what stands in place of one.
      { path: "policy-bands", element: lazyRoute(<PolicyBandsPage />) },
      // Users, roles and grants. The endpoints behind it were complete long
      // before anything rendered them, so granting a role meant an engineer
      // with database access.
      { path: "administration", element: lazyRoute(<AdministrationPage />) },
      // Tenant and practice configuration. Append-only: a change supersedes,
      // it does not overwrite.
      { path: "settings", element: lazyRoute(<SettingsPage />) },
      // (H) Your own password and your own second factor — NOT under `settings`, which is
      // gated on `tenant_setting:read`. Your password is not tenant configuration, and
      // putting it there would mean `front_desk` — the role most likely to share a
      // workstation — is the one role that cannot rotate its own credential. Reached from
      // the account menu rather than the nav rail: it is about you, not about the practice.
      { path: "security", element: lazyRoute(<SecurityPage />) },
      // The audit log. Written in the transaction with every change since the
      // beginning; nothing read it back until now.
      { path: "audit", element: lazyRoute(<AuditPage />) },
      // What the product emailed (backend D173) — beside the audit log, same reader.
      { path: "email-log", element: lazyRoute(<EmailLogPage />) },
      // Where care is delivered. The table has existed since the first migration.
      { path: "locations", element: lazyRoute(<LocationsPage />) },
      // ── The five modules that had no screen at all ──────────────────────────
      //
      // Each is the UI over a module reachable only through `/api/v1` until now.
      // Two of them are still list screens **without a list endpoint** —
      // authorizations are read through a patient and acknowledgements through a
      // claim — and each says on the page what it could and could not look at.
      // `claim-submissions` was the third and is no longer: when the collection
      // it needed arrived, the workaround was deleted rather than left beside
      // it. These two are waiting on the same thing.
      //
      // Charges used to be the third. `GET /encounters`, `GET /charges` and
      // `GET /charge-batches` landed, so `/charges` opens on the uncoded
      // worklist rather than on a field somebody pastes an encounter id into.
      { path: "charges", element: lazyRoute(<ChargeCapturePage />) },
      { path: "code-sets", element: lazyRoute(<CodeSetsPage />) },
      { path: "service-map", element: lazyRoute(<ServiceMapPage />) },
      { path: "schedule", element: lazyRoute(<SchedulePage />) },
      { path: "prior-auth", element: lazyRoute(<PriorAuthPage />) },
      { path: "referrals", element: lazyRoute(<ReferralsPage />) },
      // The screen that has to say a document was not filed. There is no
      // document store in the API and nothing here pretends otherwise.
      { path: "documents", element: lazyRoute(<DocumentsPage />) },
      {
        path: "acknowledgements",
        element: lazyRoute(<AcknowledgementsPage />),
      },
      { path: "writeback", element: lazyRoute(<WritebackQueuePage />) },
      { path: "*", element: <NotFound /> },
    ],
  },
];

/**
 * The child paths actually registered under the authenticated shell.
 *
 * Derived from `routes` rather than restated, so it cannot fall out of step
 * with them. `nav.built.test.ts` checks the sidebar's `built` flags against
 * this in both directions — a nav link to a path absent here renders the
 * not-found page, which is what it used to do for nine of thirteen links.
 */
type Routeish = {
  path?: string;
  index?: boolean;
  children?: readonly Routeish[];
};

export const APP_ROUTE_PATHS: readonly string[] = (() => {
  // Located by structure, not by index: `routes[0]` is the unauthenticated
  // sign-in route, and indexing it was this function's first bug — it produced
  // an empty list, which made every nav link look dangling.
  const shell = (routes as readonly Routeish[]).find(
    (route) => route.path === "/" && route.children !== undefined,
  );
  return (
    (shell?.children ?? [])
      .filter((child) => child.path !== "*")
      // The index route is the shell's own path, which the nav writes as "/".
      .map((child) => (child.index === true ? "" : child.path))
      .filter((path): path is string => typeof path === "string")
  );
})();

export function AppRouter() {
  useEffect(() => {
    const timer = window.setTimeout(prefetchScreens, 1500);
    return () => window.clearTimeout(timer);
  }, []);
  return <RouterProvider router={createBrowserRouter(routes)} />;
}
